import { DndContext, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Input } from "semantic-ui-react";

import { SortableConfigItem } from "modules/QuestionsDashboard/components/EditQuestionModal/components/QuestionElements/components/QuestionElementCard/components/ElementConfiguration/components/ConfigItemsList/components";

import type { DragEndEvent } from "@dnd-kit/core";

type ConfigItemsListProps = {
  title: string;
  texts: string[];
  canEdit: boolean;
  onChange: (texts: string[]) => void;
};

const getSortableId = (textIdx: number) => `config-item-${textIdx}`;

export const ConfigItemsList = ({ title, texts, canEdit, onChange }: ConfigItemsListProps) => {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } })
  );

  const [newText, setNewText] = useState("");

  const sortableIds = texts.map((_, textIdx) => getSortableId(textIdx));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const activeIdx = sortableIds.findIndex(sortableId => sortableId === active.id);
    const overIdx = sortableIds.findIndex(sortableId => sortableId === over.id);
    if (activeIdx === -1 || overIdx === -1) return;

    onChange(arrayMove(texts, activeIdx, overIdx));
  };

  const handleAdd = () => {
    const trimmedText = newText.trim();
    if (!trimmedText) return;

    onChange([...texts, trimmedText]);
    setNewText("");
  };

  return (
    <div className="ConfigItemsList">
      <label>{title}</label>

      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy} disabled={!canEdit}>
          {texts.map((text, textIdx) => (
            <SortableConfigItem
              key={sortableIds[textIdx]}
              sortableId={sortableIds[textIdx]}
              text={text}
              canEdit={canEdit}
              onRemove={() => onChange(texts.toSpliced(textIdx, 1))}
            />
          ))}
        </SortableContext>
      </DndContext>

      {canEdit && (
        <div className="config-item-add">
          <Input fluid value={newText} placeholder={title} onChange={(_, { value }) => setNewText(value)} />
          <Button type="button" color="green" fluid onClick={handleAdd}>
            <Icon className="plus" />
            {t("v8_add_item", "Add item")}
          </Button>
        </div>
      )}
    </div>
  );
};
